import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertCircle, Calendar, CheckCircle, Clock, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { complianceAPI, WORKFLOW_STAGE_LABELS } from '../lib/complianceAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

const typeLabels = {
  BAS: 'BAS',
  IAS: 'IAS',
  PAYG: 'PAYG',
  FBT: 'FBT',
  TAX_RETURN: 'Tax Return'
};

const statusColors = {
  overdue: 'bg-red-100 text-red-800 border-red-200',
  due_soon: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-muted text-muted-foreground border-border'
};

const statusIcons = {
  overdue: AlertCircle,
  due_soon: Clock,
  pending: Calendar,
  completed: CheckCircle
};

// `embedded` renders the deadline list without its own page header/stat cards so it
// can live inside the Compliance Dashboard's "List" view. Standalone (e.g. the client
// "Deadlines" page) it keeps the full header and summary cards.
export default function Compliance({ embedded = false }) {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [clients, setClients] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [formData, setFormData] = useState({
    client_id: '',
    type: 'BAS',
    period_start: '',
    period_end: '',
    due_at: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  // Edit + delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editData, setEditData] = useState({ type: 'BAS', period_start: '', period_end: '', due_at: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDeadlines();
    loadClients();
    loadWorkflows();
  }, [statusFilter, typeFilter]);

  const loadWorkflows = async () => {
    try {
      const res = await complianceAPI.workflows();
      setWorkflows(res.data || {});
    } catch (error) {
      console.error('Failed to load obligation workflows');
    }
  };

  const handleWorkflowChange = async (newStage) => {
    if (!selectedDeadline) return;
    try {
      await complianceAPI.update(selectedDeadline.id, { workflow_status: newStage });
      setSelectedDeadline({ ...selectedDeadline, workflow_status: newStage });
      toast.success('Workflow stage updated');
      loadDeadlines();
    } catch (error) {
      toast.error('Failed to update workflow stage');
    }
  };

  const loadDeadlines = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;

      const response = await complianceAPI.list(params);
      setDeadlines(response.data);
    } catch (error) {
      toast.error('Failed to load compliance deadlines');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list({ status: 'active' });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.due_at) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await complianceAPI.create({
        client_id: formData.client_id,
        type: formData.type,
        period_start: formData.period_start || null,
        period_end: formData.period_end || null,
        due_at: new Date(formData.due_at).toISOString()
      });
      
      toast.success('Deadline added successfully');
      setDialogOpen(false);
      setFormData({
        client_id: '',
        type: 'BAS',
        period_start: '',
        period_end: '',
        due_at: ''
      });
      loadDeadlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deadline');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDeadline = (deadline) => {
    setSelectedDeadline(deadline);
    setDetailDialogOpen(true);
  };

  const handleMarkComplete = async () => {
    if (!selectedDeadline) return;

    setUpdating(true);
    try {
      await complianceAPI.update(selectedDeadline.id, { status: 'completed' });
      toast.success('Deadline marked as completed');
      setDetailDialogOpen(false);
      loadDeadlines();
    } catch (error) {
      toast.error('Failed to update deadline');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenEdit = (deadline) => {
    setEditTarget(deadline);
    setEditData({
      type: deadline.type,
      period_start: deadline.period_start ? String(deadline.period_start).slice(0, 10) : '',
      period_end: deadline.period_end ? String(deadline.period_end).slice(0, 10) : '',
      due_at: deadline.due_at ? String(deadline.due_at).slice(0, 10) : '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!editData.due_at) {
      toast.error('Due date is required');
      return;
    }
    setEditSubmitting(true);
    try {
      await complianceAPI.update(editTarget.id, {
        type: editData.type,
        period_start: editData.period_start || null,
        period_end: editData.period_end || null,
        due_at: new Date(editData.due_at).toISOString(),
      });
      toast.success('Deadline updated');
      setEditDialogOpen(false);
      setEditTarget(null);
      loadDeadlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deadline');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await complianceAPI.remove(deleteTarget.id);
      toast.success('Deadline deleted');
      setDeleteTarget(null);
      loadDeadlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deadline');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate stats
  const stats = {
    overdue: deadlines.filter(d => d.status === 'overdue').length,
    due_soon: deadlines.filter(d => d.status === 'due_soon').length,
    pending: deadlines.filter(d => d.status === 'pending').length,
    completed: deadlines.filter(d => d.status === 'completed').length
  };

  if (loading) {
    return (
      <div className={embedded ? '' : 'p-6'}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {/* Header */}
      <div className={`flex items-center ${embedded ? 'justify-end' : 'justify-between'}`}>
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="compliance-title">
              Compliance Deadlines
            </h1>
            <p className="text-muted-foreground mt-1">Track BAS, IAS, PAYG, FBT, and tax return deadlines</p>
          </div>
        )}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-deadline-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Deadline
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Compliance Deadline</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client">Client *</Label>
                <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                  <SelectTrigger id="client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BAS">BAS</SelectItem>
                    <SelectItem value="IAS">IAS</SelectItem>
                    <SelectItem value="PAYG">PAYG</SelectItem>
                    <SelectItem value="FBT">FBT</SelectItem>
                    <SelectItem value="TAX_RETURN">Tax Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="period_start">Period Start</Label>
                  <Input
                    id="period_start"
                    type="date"
                    value={formData.period_start}
                    onChange={(e) => setFormData({...formData, period_start: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="period_end">Period End</Label>
                  <Input
                    id="period_end"
                    type="date"
                    value={formData.period_end}
                    onChange={(e) => setFormData({...formData, period_end: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="due_at">Due Date *</Label>
                <Input
                  id="due_at"
                  type="date"
                  value={formData.due_at}
                  onChange={(e) => setFormData({...formData, due_at: e.target.value})}
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Deadline'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status Cards */}
      {!embedded && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow" data-testid="stat-overdue">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{stats.overdue}</p>
                <p className="text-xs text-muted-foreground mt-2">Immediate attention required</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-due-soon">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Due Soon</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.due_soon}</p>
                <p className="text-xs text-muted-foreground mt-2">Within 7 days</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-pending">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">On Track</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">{stats.pending}</p>
                <p className="text-xs text-muted-foreground mt-2">More than 7 days</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="stat-completed">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold text-muted-foreground mt-2">{stats.completed}</p>
                <p className="text-xs text-muted-foreground mt-2">Successfully lodged</p>
              </div>
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Filters & Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>All Deadlines</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="due_soon">Due Soon</SelectItem>
                  <SelectItem value="pending">On Track</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]" data-testid="type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="BAS">BAS</SelectItem>
                  <SelectItem value="IAS">IAS</SelectItem>
                  <SelectItem value="PAYG">PAYG</SelectItem>
                  <SelectItem value="FBT">FBT</SelectItem>
                  <SelectItem value="TAX_RETURN">Tax Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {deadlines.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p>No deadlines found</p>
              <p className="text-sm mt-1">Create deadlines manually or generate from client profiles</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold text-foreground/90">Client</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Type</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Period</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Due Date</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Status</TableHead>
                  <TableHead className="font-semibold text-foreground/90 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deadlines.map((deadline) => {
                  const StatusIcon = statusIcons[deadline.status];
                  return (
                    <TableRow key={deadline.id} data-testid={`deadline-row-${deadline.id}`}>
                      <TableCell className="font-medium text-foreground">
                        {deadline.client_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {typeLabels[deadline.type]}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {deadline.period_start && deadline.period_end ? (
                          <span>{formatAUDate(deadline.period_start)} - {formatAUDate(deadline.period_end)}</span>
                        ) : (
                          <span className="text-muted-foreground/70">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-medium" data-testid={`deadline-due-${deadline.id}`}>
                        {formatAUDate(deadline.due_at)}
                        {deadline.compliance_period_id && <div className="text-[10px] font-normal leading-tight" data-testid={`deadline-dates-${deadline.id}`}>
                          Statutory {deadline.statutory_due_date ? formatAUDate(deadline.statutory_due_date) : '—'} · Agent {deadline.agent_due_date ? formatAUDate(deadline.agent_due_date) : '—'}
                          <br />Internal {deadline.internal_target_date ? formatAUDate(deadline.internal_target_date) : '—'} · Lodged {deadline.actual_lodgment_date ? formatAUDate(deadline.actual_lodgment_date) : '—'}
                          {deadline.task_id && <><br /><a className="underline" href={`/tasks?task=${deadline.task_id}`} data-testid={`deadline-task-${deadline.id}`}>Open task{deadline.task_status ? ` (${deadline.task_status})` : ''}</a></>}
                        </div>}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[deadline.status]}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {deadline.status === 'due_soon' ? 'Due Soon' : 
                           deadline.status === 'pending' ? 'On Track' :
                           deadline.status.charAt(0).toUpperCase() + deadline.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDeadline(deadline)} data-testid={`view-deadline-${deadline.id}`}>
                            View
                          </Button>
                          <Button variant="ghost" size="icon" title="Edit" onClick={() => handleOpenEdit(deadline)} data-testid={`edit-deadline-${deadline.id}`}>
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteTarget(deadline)} data-testid={`delete-deadline-${deadline.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail View Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deadline Details</DialogTitle>
          </DialogHeader>
          {selectedDeadline && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Client</Label>
                  <p className="font-medium text-foreground">{selectedDeadline.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Type</Label>
                  <p className="font-medium text-foreground">{typeLabels[selectedDeadline.type]}</p>
                </div>
              </div>

              {(selectedDeadline.period_start || selectedDeadline.period_end) && (
                <div>
                  <Label className="text-muted-foreground text-sm">Period</Label>
                  <p className="font-medium text-foreground">
                    {selectedDeadline.period_start && formatAUDate(selectedDeadline.period_start)}
                    {selectedDeadline.period_start && selectedDeadline.period_end && ' - '}
                    {selectedDeadline.period_end && formatAUDate(selectedDeadline.period_end)}
                  </p>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground text-sm">Due Date</Label>
                <p className="font-medium text-foreground">{formatAUDate(selectedDeadline.due_at)}</p>
              </div>

              <div>
                <Label className="text-muted-foreground text-sm">Status</Label>
                <div className="mt-1">
                  <Badge className={statusColors[selectedDeadline.status]}>
                    {selectedDeadline.status === 'due_soon' ? 'Due Soon' :
                     selectedDeadline.status === 'pending' ? 'On Track' :
                     selectedDeadline.status.charAt(0).toUpperCase() + selectedDeadline.status.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* B#11 — per-type obligation workflow stage */}
              {(workflows[selectedDeadline.type] || []).length > 0 && (
                <div>
                  <Label className="text-muted-foreground text-sm">Workflow Stage</Label>
                  <Select value={selectedDeadline.workflow_status || 'not_started'} onValueChange={handleWorkflowChange}>
                    <SelectTrigger className="mt-1" data-testid="deadline-workflow-select">
                      <SelectValue>{WORKFLOW_STAGE_LABELS[selectedDeadline.workflow_status || 'not_started'] || 'Not Started'}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {workflows[selectedDeadline.type].map((s) => (
                        <SelectItem key={s} value={s}>{WORKFLOW_STAGE_LABELS[s] || s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Close
                </Button>
                {selectedDeadline.status !== 'completed' && (
                  <Button 
                    className="bg-primary hover:bg-primary/90" 
                    onClick={handleMarkComplete}
                    disabled={updating}
                  >
                    {updating ? 'Updating...' : 'Mark as Completed'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Compliance Deadline</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <p className="text-sm font-medium text-foreground">{editTarget?.client_name}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Type *</Label>
              <Select value={editData.type} onValueChange={(value) => setEditData({ ...editData, type: value })}>
                <SelectTrigger id="edit-type" data-testid="edit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BAS">BAS</SelectItem>
                  <SelectItem value="IAS">IAS</SelectItem>
                  <SelectItem value="PAYG">PAYG</SelectItem>
                  <SelectItem value="FBT">FBT</SelectItem>
                  <SelectItem value="TAX_RETURN">Tax Return</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-period-start">Period Start</Label>
                <Input
                  id="edit-period-start"
                  type="date"
                  value={editData.period_start}
                  onChange={(e) => setEditData({ ...editData, period_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-period-end">Period End</Label>
                <Input
                  id="edit-period-end"
                  type="date"
                  value={editData.period_end}
                  onChange={(e) => setEditData({ ...editData, period_end: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-due-at">Due Date *</Label>
              <Input
                id="edit-due-at"
                type="date"
                value={editData.due_at}
                onChange={(e) => setEditData({ ...editData, due_at: e.target.value })}
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={editSubmitting} data-testid="save-edit-deadline">
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Deadline</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete the{' '}
            <span className="font-medium">{deleteTarget && typeLabels[deleteTarget.type]}</span> deadline for{' '}
            <span className="font-medium">{deleteTarget?.client_name}</span>
            {deleteTarget?.due_at && <> due {formatAUDate(deleteTarget.due_at)}</>}? This can't be undone.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirmDelete} disabled={deleting} data-testid="confirm-delete-deadline">
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
